import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

const getEnabledMenuItems = (list: HTMLElement): HTMLElement[] =>
  Array.from(list.querySelectorAll<HTMLElement>('.MuiMenuItem-root')).filter(
    (item): boolean => item.getAttribute('aria-disabled') !== 'true'
  );

const getFocusedMenuItemIndex = (items: HTMLElement[], activeElement: Element | null): number => {
  if (activeElement == null) return -1;
  return items.findIndex((item): boolean => item === activeElement || item.contains(activeElement));
};

const isFocusInSearchInput = (
  activeElement: Element | null,
  searchInput: HTMLInputElement
): boolean => activeElement === searchInput || searchInput.contains(activeElement);

/**
 * Keyboard handler for the searchable Select search field.
 * MUI MenuList cannot navigate from an input inside ListSubheader via bubbling alone;
 * we move focus onto MenuItems explicitly (matching non-searchable Select behavior).
 */
export const handleSearchKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
  menuList: HTMLUListElement | null
): void => {
  if (e.key === 'Escape') {
    return;
  }

  if (menuList == null) {
    e.stopPropagation();
    return;
  }

  const items = getEnabledMenuItems(menuList);
  const activeElement = document.activeElement;
  const searchInput = e.currentTarget;
  const focusedIndex = getFocusedMenuItemIndex(items, activeElement);
  const focusInSearch = isFocusInSearchInput(activeElement, searchInput);

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    e.stopPropagation();
    if (items.length === 0) return;
    const nextIndex = focusedIndex === -1 ? 0 : (focusedIndex + 1) % items.length;
    items[nextIndex]?.focus();
    return;
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault();
    e.stopPropagation();
    if (focusInSearch || focusedIndex <= 0) {
      searchInput.focus();
      return;
    }
    items[focusedIndex - 1]?.focus();
    return;
  }

  if (e.key === 'Enter') {
    if (focusedIndex >= 0 && !focusInSearch) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (items.length === 0) return;
    const target = focusedIndex >= 0 ? items[focusedIndex] : items[0];
    target.click();
    return;
  }

  e.stopPropagation();
};

export type MuiFormSelectOptionT = {
  value: string | number;
  label: string;
  icon?: ReactNode;
  /** Optional MUI sx for icon/display (e.g. color), used in menu and when renderSelectedValue uses it */
  sxProps?: SxProps<Theme>;
  disabled?: boolean;
  disabledTooltip?: string;
};

type MuiFormSelectPropsT = {
  name: string;
  label: string;
  options: MuiFormSelectOptionT[];
  placeholder?: string;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** When true, show a search bar at the top of the dropdown */
  searchable?: boolean;
  /** How to render each option in the dropdown list (default: icon + label) */
  renderOption?: (option: MuiFormSelectOptionT) => ReactNode;
  /** How to render the selected value in the trigger (e.g. icon-only); when set, overrides renderOption for display */
  renderSelectedValue?: (option: MuiFormSelectOptionT) => ReactNode;
  /** Extra sx passed to the Select (e.g. compact height to match WarrQueue) */
  sx?: SxProps<Theme>;
  /** Called when the selected value changes (after form state is updated) */
  onChange?: (value: string | number) => void;
};

const MuiFormSelect = ({
  name,
  label,
  options,
  placeholder = '',
  isLoading = false,
  required = false,
  disabled = false,
  searchable = false,
  renderOption,
  renderSelectedValue,
  sx: sxProp,
  onChange: onChangeProp,
}: MuiFormSelectPropsT): JSX.Element => {
  const { control } = useFormContext();
  const [searchQuery, setSearchQuery] = useState('');
  const menuListRef = useRef<HTMLUListElement | null>(null);

  // Use a ref so renderValue always reads the latest options,
  // even if Controller skips a re-render when only options change.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const renderSelectedValueRef = useRef(renderSelectedValue);
  renderSelectedValueRef.current = renderSelectedValue;

  const renderOptionRef = useRef(renderOption);
  renderOptionRef.current = renderOption;

  const filteredOptions = useMemo((): MuiFormSelectOptionT[] => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, searchQuery, searchable]);

  const baseSx = {
    '& .MuiSelect-select': { fontSize: '13px' },
    '& .MuiInputLabel-root': { fontSize: '13px' },
    '& .MuiFormHelperText-root': { fontSize: '11px' },
  };
  let sxExtra: SxProps<Theme>[] = [];
  if (sxProp != null) {
    sxExtra = Array.isArray(sxProp) ? sxProp : [sxProp];
  }
  const resolvedSx = [baseSx, ...sxExtra] as SxProps<Theme>;

  const handleRenderValue = (v: unknown): React.ReactNode => {
    if (v === '' || v == null) {
      return placeholder ? (
        <Box component="span" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      ) : (
        <Box component="span" />
      );
    }
    const currentOptions = optionsRef.current;
    const opt = currentOptions.find(o => String(o.value) === String(v));
    // If the option isn't found (e.g. options still loading), show nothing rather than raw value
    if (opt == null) return <Box component="span" />;
    const rsv = renderSelectedValueRef.current;
    if (rsv != null) return rsv(opt);
    const ro = renderOptionRef.current;
    const content = ro != null ? ro(opt) : opt.label;
    // For plain string labels, keep the field at its container width by truncating
    // with an ellipsis and exposing the full value via a tooltip on hover. Custom
    // renderers are returned as-is so callers can control their own layout.
    if (typeof content === 'string') {
      return (
        <Tooltip title={content} placement="top" arrow enterDelay={300}>
          <Box
            component="span"
            sx={{
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {content}
          </Box>
        </Tooltip>
      );
    }
    return content;
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field, fieldState }): React.ReactElement => {
        const hasValue = field.value != null && field.value !== '';
        // Only pass the value to <Select> when it exists in the current options.
        // Otherwise pass '' to avoid MUI's "out-of-range value" dev warning when
        // the form is prepopulated with a stale id (options still loading, or
        // backend value no longer valid for the selected category).
        const selectValue =
          hasValue && options.some(o => String(o.value) === String(field.value)) ? field.value : '';
        return (
          <Box>
            <FormControl
              fullWidth
              size="small"
              error={!!fieldState.error}
              disabled={disabled || isLoading}
            >
              <InputLabel
                id={`select-label-${name}`}
                shrink={hasValue || undefined}
                required={required}
                sx={{ fontSize: '13px' }}
              >
                {label}
              </InputLabel>
              <Select
                {...field}
                labelId={`select-label-${name}`}
                label={label}
                value={selectValue}
                displayEmpty
                notched={hasValue || undefined}
                renderValue={handleRenderValue}
                onClose={(): void => setSearchQuery('')}
                onChange={(e): void => {
                  field.onChange(e);
                  const v = e.target.value;
                  if (v !== '' && v != null) {
                    onChangeProp?.(v as string | number);
                  }
                }}
                MenuProps={{
                  autoFocus: !searchable,
                  MenuListProps: {
                    ref: menuListRef,
                  },
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 0.5,
                      ...(searchable ? { maxHeight: 380 } : {}),
                    },
                  },
                }}
                sx={resolvedSx}
              >
                {searchable && (
                  <ListSubheader sx={{ p: 1, lineHeight: 'unset' }}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      variant="outlined"
                      fullWidth
                      autoFocus
                      inputProps={{
                        onKeyDown: (e: KeyboardEvent<HTMLInputElement>): void => {
                          handleSearchKeyDown(e, menuListRef.current);
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Clear search"
                              size="small"
                              onClick={handleClearSearch}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </ListSubheader>
                )}
                {filteredOptions.map(option => {
                  const menuItem = (
                    <MenuItem
                      key={String(option.value)}
                      value={option.value}
                      disabled={option.disabled === true}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {option.icon != null && (
                          <Box sx={option.sxProps ?? undefined} display="flex" alignItems="center">
                            {option.icon}
                          </Box>
                        )}
                        <Box component="span" sx={{ color: 'inherit', fontSize: '13px' }}>
                          {option.label}
                        </Box>
                      </Box>
                    </MenuItem>
                  );

                  if (option.disabled && option.disabledTooltip) {
                    return (
                      <Tooltip key={String(option.value)} title={option.disabledTooltip} arrow>
                        <span>{menuItem}</span>
                      </Tooltip>
                    );
                  }

                  return menuItem;
                })}
                {searchable && searchQuery.trim() !== '' && filteredOptions.length === 0 && (
                  <MenuItem disabled>
                    <Box component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      No results match your search.
                    </Box>
                  </MenuItem>
                )}
              </Select>
              {fieldState.error?.message != null && (
                <FormHelperText error>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );
      }}
    />
  );
};

export default MuiFormSelect;
